import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { 
  CheckCircle, 
  XCircle, 
  Users, 
  Building2, 
  Calendar, 
  Gift,
  Plus,
  Edit,
  Trash2,
  Percent,
  Star,
  Crown,
  Tag,
  Zap,
  Ticket,
  Award,
  Sparkles,
  Mail,
  Phone,
  Clock,
  Eye,
  MapPin,
  IndianRupee,
  MessageSquare,
  Shield,
  Send,
  AlertTriangle,
  CheckCircle2
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

// Icon options for offers
const ICON_OPTIONS = [
  { value: "percent", label: "Percent", icon: Percent },
  { value: "star", label: "Star", icon: Star },
  { value: "crown", label: "Crown", icon: Crown },
  { value: "gift", label: "Gift", icon: Gift },
  { value: "tag", label: "Tag", icon: Tag },
  { value: "zap", label: "Zap", icon: Zap },
  { value: "ticket", label: "Ticket", icon: Ticket },
  { value: "award", label: "Award", icon: Award },
  { value: "sparkles", label: "Sparkles", icon: Sparkles },
  { value: "users", label: "Users", icon: Users },
];

// Color options for offers
const COLOR_OPTIONS = [
  { value: "blue", label: "Blue", class: "bg-blue-500" },
  { value: "green", label: "Green", class: "bg-green-500" },
  { value: "emerald", label: "Emerald", class: "bg-emerald-500" },
  { value: "amber", label: "Amber", class: "bg-amber-500" },
  { value: "purple", label: "Purple", class: "bg-purple-500" },
  { value: "red", label: "Red", class: "bg-red-500" },
  { value: "orange", label: "Orange", class: "bg-orange-500" },
  { value: "pink", label: "Pink", class: "bg-pink-500" },
];

const OFFER_TYPES = [
  { value: "discount", label: "Discount" },
  { value: "referral", label: "Referral Bonus" },
  { value: "premium_trial", label: "Premium Trial" },
  { value: "vip", label: "VIP Program" },
  { value: "free_listing", label: "Free Listing" },
  { value: "cashback", label: "Cashback" },
];

const TARGET_AUDIENCE = [
  { value: "all", label: "All Users" },
  { value: "new_users", label: "New Users Only" },
  { value: "sellers", label: "Sellers Only" },
  { value: "buyers", label: "Buyers Only" },
  { value: "premium", label: "Premium Users" },
];

export default function AdminPanel() {
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [adminMessage, setAdminMessage] = useState("");
  const [sendingAdminMessage, setSendingAdminMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  
  // Offer form state
  const [offerDialog, setOfferDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerForm, setOfferForm] = useState({
    title: "",
    description: "",
    offer_type: "discount",
    icon: "percent",
    color: "blue",
    badge: "",
    value: "",
    code: "",
    link: "",
    target_audience: "all",
    priority: 0,
    active: true,
    expires_at: ""
  });
  const [submittingOffer, setSubmittingOffer] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, usersRes, listingsRes, bookingsRes, offersRes, conversationsRes] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/users"),
        api.get("/admin/listings"),
        api.get("/admin/bookings"),
        api.get("/admin/offers"),
        api.get("/admin/conversations").catch(() => ({ data: [] }))
      ]);
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data);
      setListings(listingsRes.data);
      setBookings(bookingsRes.data);
      setOffers(offersRes.data);
      setConversations(conversationsRes.data);
    } catch (error) {
      toast.error("Failed to fetch admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (listingId, approved) => {
    try {
      await api.patch(`/admin/listings/${listingId}/approve?approved=${approved}`);
      toast.success(`Listing ${approved ? "approved" : "rejected"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update listing");
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "N/A";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Conversation management functions
  const fetchConversationMessages = async (conversationId) => {
    try {
      const { data } = await api.get(`/admin/conversations/${conversationId}/messages`);
      setSelectedConversation(data.conversation);
      setConversationMessages(data.messages);
    } catch (error) {
      toast.error("Failed to fetch conversation messages");
    }
  };

  const handleSendAdminMessage = async (e) => {
    e.preventDefault();
    if (!adminMessage.trim() || !selectedConversation) return;
    
    setSendingAdminMessage(true);
    try {
      const formData = new FormData();
      formData.append('content', adminMessage);
      await api.post(`/admin/conversations/${selectedConversation.id}/messages`, formData);
      toast.success("Message sent as ReBusi Team");
      setAdminMessage("");
      fetchConversationMessages(selectedConversation.id);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingAdminMessage(false);
    }
  };

  const toggleContactSharing = async (conversationId, allowed) => {
    try {
      await api.patch(`/admin/conversations/${conversationId}/allow-contact?allowed=${allowed}`);
      toast.success(`Contact sharing ${allowed ? 'enabled' : 'disabled'}`);
      fetchData();
      if (selectedConversation?.id === conversationId) {
        fetchConversationMessages(conversationId);
      }
    } catch (error) {
      toast.error("Failed to update contact sharing");
    }
  };

  // Offer management functions
  const openCreateOffer = () => {
    setEditingOffer(null);
    setOfferForm({
      title: "",
      description: "",
      offer_type: "discount",
      icon: "percent",
      color: "blue",
      badge: "",
      value: "",
      code: "",
      link: "",
      target_audience: "all",
      priority: 0,
      active: true,
      expires_at: ""
    });
    setOfferDialog(true);
  };

  const openEditOffer = (offer) => {
    setEditingOffer(offer);
    setOfferForm({
      title: offer.title,
      description: offer.description,
      offer_type: offer.offer_type,
      icon: offer.icon,
      color: offer.color,
      badge: offer.badge || "",
      value: offer.value || "",
      code: offer.code || "",
      link: offer.link || "",
      target_audience: offer.target_audience,
      priority: offer.priority,
      active: offer.active,
      expires_at: offer.expires_at ? offer.expires_at.split('T')[0] : ""
    });
    setOfferDialog(true);
  };

  const handleSubmitOffer = async (e) => {
    e.preventDefault();
    setSubmittingOffer(true);

    try {
      const payload = {
        ...offerForm,
        priority: parseInt(offerForm.priority) || 0,
        expires_at: offerForm.expires_at ? new Date(offerForm.expires_at).toISOString() : null,
        badge: offerForm.badge || null,
        value: offerForm.value || null,
        code: offerForm.code || null,
        link: offerForm.link || null
      };

      if (editingOffer) {
        await api.patch(`/admin/offers/${editingOffer.id}`, payload);
        toast.success("Offer updated successfully");
      } else {
        await api.post("/admin/offers", payload);
        toast.success("Offer created successfully");
      }

      setOfferDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save offer");
    } finally {
      setSubmittingOffer(false);
    }
  };

  const handleToggleOffer = async (offerId) => {
    try {
      const { data } = await api.patch(`/admin/offers/${offerId}/toggle`);
      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to toggle offer status");
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!confirm("Are you sure you want to delete this offer?")) return;

    try {
      await api.delete(`/admin/offers/${offerId}`);
      toast.success("Offer deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete offer");
    }
  };

  const getIconComponent = (iconName) => {
    const option = ICON_OPTIONS.find(o => o.value === iconName);
    return option ? option.icon : Percent;
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-500";
      case "seller": return "bg-blue-500";
      case "buyer": return "bg-green-500";
      case "consultant": return "bg-purple-500";
      default: return "bg-slate-500";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "approved": case "active": case "confirmed": return "bg-emerald-500";
      case "pending": return "bg-amber-500";
      case "rejected": case "cancelled": return "bg-red-500";
      case "completed": return "bg-blue-500";
      default: return "bg-slate-500";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-20">
        <h1 className="text-3xl font-bold text-slate-900 mb-6" data-testid="admin-header">Admin Panel</h1>

        {/* Analytics Cards - Clickable */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="analytics-cards">
            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg ${activeTab === 'users' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setActiveTab('users')}
              data-testid="analytics-users"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Total Users</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.total_users}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg ${activeTab === 'listings' ? 'ring-2 ring-emerald-500' : ''}`}
              onClick={() => setActiveTab('listings')}
              data-testid="analytics-listings"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Total Listings</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.total_listings}</p>
                  </div>
                  <Building2 className="w-8 h-8 text-emerald-600" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg ${activeTab === 'bookings' ? 'ring-2 ring-purple-500' : ''}`}
              onClick={() => setActiveTab('bookings')}
              data-testid="analytics-bookings"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Total Bookings</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.total_bookings}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg ${activeTab === 'offers' ? 'ring-2 ring-amber-500' : ''}`}
              onClick={() => setActiveTab('offers')}
              data-testid="analytics-offers"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Active Offers</p>
                    <p className="text-2xl font-bold text-slate-900">{offers.filter(o => o.active).length}</p>
                  </div>
                  <Gift className="w-8 h-8 text-amber-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border w-full justify-start overflow-x-auto">
            <TabsTrigger value="users" className="flex items-center gap-2" data-testid="tab-users">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="listings" className="flex items-center gap-2" data-testid="tab-listings">
              <Building2 className="w-4 h-4" />
              Listings
            </TabsTrigger>
            <TabsTrigger value="bookings" className="flex items-center gap-2" data-testid="tab-bookings">
              <Calendar className="w-4 h-4" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="offers" className="flex items-center gap-2" data-testid="tab-offers">
              <Gift className="w-4 h-4" />
              Offers
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="tab-messages">
              <MessageSquare className="w-4 h-4" />
              Messages
              {conversations.length > 0 && (
                <Badge variant="secondary" className="ml-1">{conversations.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card data-testid="users-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  All Users ({users.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No users found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Mobile</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Joined</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                            <TableCell className="font-medium">{user.name}</TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 text-sm text-slate-600">
                                <Mail className="w-3 h-3" />
                                {user.email}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 text-sm text-slate-600">
                                <Phone className="w-3 h-3" />
                                {user.mobile || "N/A"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${getRoleBadgeColor(user.role)} text-white text-xs`}>
                                {user.role}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatDate(user.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings">
            <Card data-testid="listings-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                  All Listings ({listings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {listings.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No listings found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Business Name</TableHead>
                          <TableHead>Industry</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Asking Price</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Listed</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {listings.map((listing) => (
                          <TableRow key={listing.id} data-testid={`listing-row-${listing.id}`}>
                            <TableCell className="font-medium">{listing.name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{listing.industry}</TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 text-sm text-slate-600">
                                <MapPin className="w-3 h-3" />
                                {listing.location}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 font-medium text-slate-900">
                                <IndianRupee className="w-3 h-3" />
                                {formatCurrency(listing.asking_price).replace('₹', '')}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${getStatusBadgeColor(listing.status)} text-white text-xs`}>
                                {listing.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatDate(listing.created_at)}
                            </TableCell>
                            <TableCell>
                              {listing.status === "pending" && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproval(listing.id, true)}
                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2"
                                    data-testid={`approve-${listing.id}`}
                                  >
                                    <CheckCircle className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleApproval(listing.id, false)}
                                    className="border-red-200 text-red-600 hover:bg-red-50 h-7 px-2"
                                    data-testid={`reject-${listing.id}`}
                                  >
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                              {listing.status !== "pending" && (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card data-testid="bookings-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  All Bookings ({bookings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No bookings found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Consultant</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.map((booking) => (
                          <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                            <TableCell className="font-medium">{booking.consultant_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{booking.buyer_name}</TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 text-sm">
                                <Calendar className="w-3 h-3" />
                                {formatDate(booking.date)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 text-sm text-slate-600">
                                <Clock className="w-3 h-3" />
                                {booking.time_slot}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${getStatusBadgeColor(booking.status)} text-white text-xs`}>
                                {booking.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatDate(booking.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <Card data-testid="offers-management-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-amber-500" />
                  Promotional Offers ({offers.length})
                </CardTitle>
                <Button onClick={openCreateOffer} className="bg-amber-500 hover:bg-amber-600" data-testid="create-offer-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Offer
                </Button>
              </CardHeader>
              <CardContent>
                {offers.length === 0 ? (
                  <div className="text-center py-12">
                    <Gift className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No offers created yet</p>
                    <Button onClick={openCreateOffer} variant="outline">
                      Create Your First Offer
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {offers.map((offer) => {
                      const IconComp = getIconComponent(offer.icon);
                      const colorOption = COLOR_OPTIONS.find(c => c.value === offer.color);
                      return (
                        <div 
                          key={offer.id} 
                          className={`border rounded-lg p-4 ${offer.active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`}
                          data-testid={`offer-item-${offer.id}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg ${colorOption?.class || 'bg-blue-500'} bg-opacity-20`}>
                              <IconComp className={`w-6 h-6 ${colorOption?.class?.replace('bg-', 'text-') || 'text-blue-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-slate-900">{offer.title}</h3>
                                {offer.badge && (
                                  <Badge variant="secondary" className="text-xs">{offer.badge}</Badge>
                                )}
                                <Badge variant={offer.active ? "default" : "outline"} className={offer.active ? "bg-emerald-500" : ""}>
                                  {offer.active ? "Active" : "Inactive"}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600 mb-2">{offer.description}</p>
                              <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                                <span className="bg-slate-100 px-2 py-1 rounded">
                                  Type: {OFFER_TYPES.find(t => t.value === offer.offer_type)?.label || offer.offer_type}
                                </span>
                                <span className="bg-slate-100 px-2 py-1 rounded">
                                  Audience: {TARGET_AUDIENCE.find(t => t.value === offer.target_audience)?.label || offer.target_audience}
                                </span>
                                {offer.code && (
                                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded font-mono">
                                    Code: {offer.code}
                                  </span>
                                )}
                                {offer.value && (
                                  <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                                    Value: {offer.value}
                                  </span>
                                )}
                                {offer.expires_at && (
                                  <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded">
                                    Expires: {new Date(offer.expires_at).toLocaleDateString()}
                                  </span>
                                )}
                                <span className="bg-slate-100 px-2 py-1 rounded">
                                  Priority: {offer.priority}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleOffer(offer.id)}
                                data-testid={`toggle-offer-${offer.id}`}
                              >
                                {offer.active ? "Deactivate" : "Activate"}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditOffer(offer)}
                                data-testid={`edit-offer-${offer.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteOffer(offer.id)}
                                data-testid={`delete-offer-${offer.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab - Admin Conversation Monitoring */}
          <TabsContent value="messages">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Conversations List */}
              <Card className="md:col-span-1" data-testid="admin-conversations-list">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    All Conversations
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    {conversations.length === 0 ? (
                      <div className="p-6 text-center text-slate-500">
                        <MessageSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                        <p>No conversations yet</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {conversations.map((conv) => (
                          <div
                            key={conv.id}
                            onClick={() => fetchConversationMessages(conv.id)}
                            className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                              selectedConversation?.id === conv.id ? "bg-blue-50 border-l-4 border-blue-600" : ""
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-semibold text-slate-900 text-sm">
                                {Object.values(conv.participant_names || {}).join(" ↔ ")}
                              </h3>
                              {conv.flagged_count > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  {conv.flagged_count}
                                </Badge>
                              )}
                            </div>
                            {conv.listing_name && (
                              <p className="text-xs text-blue-600 font-medium">Re: {conv.listing_name}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              {conv.is_contact_sharing_allowed ? (
                                <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Contact OK
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                  <Shield className="w-3 h-3 mr-1" />
                                  Protected
                                </Badge>
                              )}
                              <span className="text-xs text-slate-400">
                                {formatDate(conv.updated_at)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Conversation Messages */}
              <Card className="md:col-span-2" data-testid="admin-messages-panel">
                {selectedConversation ? (
                  <>
                    <CardHeader className="border-b border-slate-200 pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">
                          {Object.values(selectedConversation.participant_names || {}).join(" ↔ ")}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">Contact Sharing:</span>
                          <Switch
                            checked={selectedConversation.is_contact_sharing_allowed}
                            onCheckedChange={(checked) => toggleContactSharing(selectedConversation.id, checked)}
                          />
                        </div>
                      </div>
                      {selectedConversation.listing_name && (
                        <p className="text-sm text-blue-600">Regarding: {selectedConversation.listing_name}</p>
                      )}
                      {selectedConversation.deal_stage && (
                        <Badge variant="secondary">Deal Stage: {selectedConversation.deal_stage}</Badge>
                      )}
                    </CardHeader>
                    <CardContent className="p-0 flex flex-col h-[420px]">
                      <ScrollArea className="flex-1 p-4">
                        <div className="space-y-3">
                          {conversationMessages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.is_admin_message ? "justify-center" : "justify-start"}`}
                            >
                              {msg.is_admin_message ? (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 max-w-[85%]">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Shield className="w-4 h-4 text-emerald-600" />
                                    <span className="text-xs font-semibold text-emerald-700">ReBusi Team</span>
                                  </div>
                                  <p className="text-sm text-slate-700">{msg.content}</p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {new Date(msg.created_at).toLocaleString()}
                                  </p>
                                </div>
                              ) : (
                                <div className="bg-slate-100 rounded-lg p-3 max-w-[80%]">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-semibold text-slate-700">{msg.sender_name}</span>
                                    <span className="text-xs text-slate-400">→</span>
                                    <span className="text-xs text-slate-500">{msg.receiver_name}</span>
                                  </div>
                                  <p className="text-sm text-slate-900">{msg.content}</p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {new Date(msg.created_at).toLocaleString()}
                                  </p>
                                  {msg.flagged && (
                                    <Badge variant="destructive" className="mt-2 text-xs">
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      Flagged: {msg.flag_reason}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      
                      {/* Admin Message Input */}
                      <form onSubmit={handleSendAdminMessage} className="p-4 border-t border-slate-200 bg-emerald-50">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="w-4 h-4 text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-700">Send as ReBusi Team</span>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={adminMessage}
                            onChange={(e) => setAdminMessage(e.target.value)}
                            placeholder="Type a message to both parties..."
                            className="bg-white"
                            data-testid="admin-message-input"
                          />
                          <Button 
                            type="submit" 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            disabled={sendingAdminMessage || !adminMessage.trim()}
                            data-testid="admin-send-button"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-[500px]">
                    <div className="text-center text-slate-500">
                      <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                      <p className="font-medium">Select a conversation to monitor</p>
                      <p className="text-sm mt-1">You can view all messages and intervene when needed</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Offer Create/Edit Dialog */}
      <Dialog open={offerDialog} onOpenChange={setOfferDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Gift className="w-6 h-6 text-amber-500" />
              {editingOffer ? "Edit Offer" : "Create New Offer"}
            </DialogTitle>
            <DialogDescription>
              {editingOffer 
                ? "Update the promotional offer details" 
                : "Create a new promotional offer for users"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmitOffer} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="offer-title">Title *</Label>
                <Input
                  id="offer-title"
                  placeholder="e.g., Get 20% Off Your First Listing"
                  value={offerForm.title}
                  onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })}
                  required
                  data-testid="offer-title-input"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label htmlFor="offer-description">Description *</Label>
                <Textarea
                  id="offer-description"
                  rows={2}
                  placeholder="Brief description of the offer"
                  value={offerForm.description}
                  onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })}
                  required
                  data-testid="offer-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Offer Type *</Label>
                <Select
                  value={offerForm.offer_type}
                  onValueChange={(val) => setOfferForm({ ...offerForm, offer_type: val })}
                >
                  <SelectTrigger data-testid="offer-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OFFER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Target Audience *</Label>
                <Select
                  value={offerForm.target_audience}
                  onValueChange={(val) => setOfferForm({ ...offerForm, target_audience: val })}
                >
                  <SelectTrigger data-testid="offer-audience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TARGET_AUDIENCE.map((aud) => (
                      <SelectItem key={aud.value} value={aud.value}>{aud.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={offerForm.icon}
                  onValueChange={(val) => setOfferForm({ ...offerForm, icon: val })}
                >
                  <SelectTrigger data-testid="offer-icon-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((opt) => {
                      const Icon = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {opt.label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={offerForm.color}
                  onValueChange={(val) => setOfferForm({ ...offerForm, color: val })}
                >
                  <SelectTrigger data-testid="offer-color-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLOR_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded ${opt.class}`} />
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-badge">Badge (optional)</Label>
                <Input
                  id="offer-badge"
                  placeholder="e.g., New, Limited, Hot"
                  value={offerForm.badge}
                  onChange={(e) => setOfferForm({ ...offerForm, badge: e.target.value })}
                  data-testid="offer-badge-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-value">Value Display (optional)</Label>
                <Input
                  id="offer-value"
                  placeholder="e.g., ₹500, 20%, 7 days"
                  value={offerForm.value}
                  onChange={(e) => setOfferForm({ ...offerForm, value: e.target.value })}
                  data-testid="offer-value-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-code">Promo Code (optional)</Label>
                <Input
                  id="offer-code"
                  placeholder="e.g., SAVE20"
                  value={offerForm.code}
                  onChange={(e) => setOfferForm({ ...offerForm, code: e.target.value.toUpperCase() })}
                  data-testid="offer-code-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-priority">Priority</Label>
                <Input
                  id="offer-priority"
                  type="number"
                  min="0"
                  placeholder="0 (higher = shown first)"
                  value={offerForm.priority}
                  onChange={(e) => setOfferForm({ ...offerForm, priority: e.target.value })}
                  data-testid="offer-priority-input"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label htmlFor="offer-link">Action Link (optional)</Label>
                <Input
                  id="offer-link"
                  placeholder="e.g., /list-business, /marketplace"
                  value={offerForm.link}
                  onChange={(e) => setOfferForm({ ...offerForm, link: e.target.value })}
                  data-testid="offer-link-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-expires">Expires On (optional)</Label>
                <Input
                  id="offer-expires"
                  type="date"
                  value={offerForm.expires_at}
                  onChange={(e) => setOfferForm({ ...offerForm, expires_at: e.target.value })}
                  data-testid="offer-expires-input"
                />
              </div>

              <div className="flex items-center space-x-3 pt-6">
                <Switch
                  id="offer-active"
                  checked={offerForm.active}
                  onCheckedChange={(checked) => setOfferForm({ ...offerForm, active: checked })}
                  data-testid="offer-active-switch"
                />
                <Label htmlFor="offer-active" className="cursor-pointer">
                  Active (visible to users)
                </Label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setOfferDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-amber-500 hover:bg-amber-600"
                disabled={submittingOffer}
                data-testid="submit-offer-btn"
              >
                {submittingOffer ? "Saving..." : (editingOffer ? "Update Offer" : "Create Offer")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
